import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, MapPin } from 'lucide-react';

const HERO_VIDEO = "https://video.wixstatic.com/video/720c8e_9f5d85b923c64cfd8fedd4cc1490a3ac/480p/mp4/file.mp4";
const HERO_POSTER = "https://static.wixstatic.com/media/720c8e_9f5d85b923c64cfd8fedd4cc1490a3acf000.jpg/v1/fill/w_1600,h_900,al_c,q_85,enc_avif,quality_auto/720c8e_9f5d85b923c64cfd8fedd4cc1490a3acf000.jpg";

const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(`#${id}`, { offset: -72 });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }
};

const MaskedLine = ({ children, index, className = '' }) => (
  <div className="overflow-hidden">
    <motion.div
      custom={index}
      initial={{ y: '115%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + index * 0.16 }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });

  const videoY = useTransform(scrollYProgress, [0, 1], ['0%', '28%']);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '55%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
      style={{ backgroundColor: 'var(--espresso)' }}
    >
      {/* Parallax video background */}
      <motion.div className="absolute inset-0" style={{ y: videoY, scale: videoScale }}>
        <video
          className="h-full w-full object-cover"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
      </motion.div>

      {/* Espresso overlays for legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#14100d]/80 via-[#14100d]/45 to-[#14100d]" />
      <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_50%_38%,transparent_40%,rgba(13,10,8,0.72)_100%)]" />

      {/* Content */}
      <motion.div
        className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <MaskedLine index={0}>
          <p
            data-testid="hero-eyebrow"
            className="font-body text-[11px] font-medium uppercase tracking-[0.45em] text-[#f2a33c] sm:text-xs"
          >
            Pottstown, PA — A Social Innovation Hub
          </p>
        </MaskedLine>

        <h1 className="mt-6 leading-[0.86]">
          <MaskedLine index={1}>
            <span
              data-testid="hero-title"
              className="font-display block text-[clamp(4.5rem,17vw,13rem)] font-black tracking-[-0.03em] text-[#f4ede3]"
            >
              VIBE
            </span>
          </MaskedLine>
          <MaskedLine index={2}>
            <span className="font-display block text-[clamp(2.4rem,8vw,5.5rem)] font-light italic tracking-tight text-[#f2a33c]">
              café
            </span>
          </MaskedLine>
        </h1>

        <MaskedLine index={3}>
          <p
            data-testid="hero-tagline"
            className="font-body mx-auto mt-8 max-w-2xl text-sm uppercase tracking-[0.3em] text-[#b8ac9c] sm:text-base"
          >
            Where Community · Art · Culture Collide
          </p>
        </MaskedLine>

        <MaskedLine index={4}>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row">
            <button
              data-testid="hero-book-button"
              className="btn-pill font-body"
              onClick={() => scrollToId('booking')}
            >
              Book a Space
            </button>
            <button
              data-testid="hero-explore-button"
              className="btn-pill-outline font-body"
              onClick={() => scrollToId('services')}
            >
              Explore the Hub
            </button>
          </div>
        </MaskedLine>
      </motion.div>

      {/* Bottom meta row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute inset-x-0 bottom-0 z-10 flex items-end justify-between px-6 pb-6 sm:px-10"
      >
        <div className="font-body hidden items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#b8ac9c] sm:flex">
          <MapPin size={13} className="text-[#f2a33c]" />
          249 High St · Pottstown
        </div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
          className="font-body flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#b8ac9c]"
        >
          Scroll <ArrowDown size={14} className="text-[#f2a33c]" />
        </motion.div>
        <div className="font-body hidden text-[11px] uppercase tracking-[0.25em] text-[#b8ac9c] sm:block">
          Open Mon – Sat
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
